import { DownloadIcon } from 'lucide-react';
import { Button } from '../../Button';
import { PageSizeSelect, PaginationCounter, Paginator } from '../../Paginator';
import { Tooltip } from '../../Tooltip';
import { cn } from '../../../lib/utils';
import useI18n from '../../../hooks/useI18n';
import type { TableProps } from '../types';
import type { RowData, Table } from '@tanstack/react-table';

type TableFooterProps<TData extends RowData> = {
  table: Table<TData>;
  exportTableData?: () => void;
  isFullscreen: boolean;
} & Pick<TableProps<RowData>, 'disableExport' | 'rowsPerPageOptions'>;

export function Footer<TData extends RowData>({
  table,
  exportTableData,
  disableExport,
  rowsPerPageOptions,
  isFullscreen,
}: TableFooterProps<TData>) {
  const { t } = useI18n();

  return (
    <div
      className={cn(
        'eye:my-8 eye:grid eye:grid-cols-3 eye:items-center eye:justify-between eye:gap-y-12',
        isFullscreen && 'eye:px-8 eye:pb-8',
      )}
    >
      {table.getPrePaginationRowModel().rows.length > 1 && (
        <>
          <div>
            {exportTableData && (
              <Tooltip content={t('exportAllRows')}>
                <Button
                  disabled={disableExport}
                  variant='ghost'
                  size='icon'
                  onClick={() => exportTableData()}
                >
                  <DownloadIcon aria-label={t('exportAllRows')} />
                </Button>
              </Tooltip>
            )}
          </div>

          <div className='eye:flex eye:justify-center'>
            <PaginationCounter
              className='eye:hidden eye:md:block'
              page={(table.getState().pagination.pageIndex ?? 0) + 1}
              pageSize={table.getState().pagination.pageSize}
              totalItems={table.getPrePaginationRowModel().rows.length}
            />
          </div>

          <PageSizeSelect
            className='eye:justify-end'
            value={table.getState().pagination.pageSize}
            options={rowsPerPageOptions}
            onValueChange={(value) => table.setPageSize(value)}
          />

          <Paginator
            className='eye:col-span-3'
            page={table.getState().pagination.pageIndex + 1}
            pageCount={table.getPageCount()}
            onPageChange={(page) => table.setPageIndex(page - 1)}
          />
        </>
      )}
    </div>
  );
}
